import type { MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import { Locale as UiLocale } from '@tix-ticketing/ui-theme';
import { useRouter } from 'next/router';
import type { Dispatch, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AppSettingsDto, EventDto, OrderResponse, TicketDto, TokensResponse } from 'api/generated';
import { parseDedicatedOrderRoute } from 'lib/dedicated-order';
import { getAllEventTicketGroups, isActiveReservation } from 'lib/helpers/order-helpers';
import { allLocales, fullToShortLocale, shortToFullLocale } from 'messages';
import { parseNumericQueryParam } from './helpers/query-helpers';
import { useLocalStorage, useSessionStorage } from './hooks/use-storage';
import { useTempDataExpiry } from './hooks/use-temp-data-expiry';
export type EditingMode = 'reserve' | 'order' | 'receipt';
export type TempEventReservationData = {
  eventId: number;
  tickets: TicketDto[];
  editingMode?: EditingMode;
};
export interface SubscriptionSelectedEvent {
  eventGroupId: number;
  eventId: number;
  selectedDate?: string;
  hallId?: number;
  isNumbered?: boolean;
}

/** The new event (and optional date) the customer picked to exchange their
 *  tickets into, chosen on the "Choose a new event" step. */
export interface ExchangeSelectedEvent {
  eventGroupId: number;
  eventId: number;
  eventName?: string;
  selectedDate?: string;
  hallId?: number;
  isNumbered?: boolean;
}

/** Per-tab context for the change-tickets (exchange) flow, carried from the
 *  "Choose a new event" step into the seat picker and cart. */
export type TempExchangeData = {
  /** The event the customer currently holds tickets for (exchanging FROM). */
  sourceEventId: number;
  /** Encrypted id of the original paid order the tickets belong to. Required
   *  to start the exchange and drive every later exchange endpoint. */
  sourceEncryptedOrderId: string;
  /** Name of the source event, shown in the sticky action bar. */
  sourceEventName?: string;
  /** Subscription type of the ticket group being exchanged, or undefined for
   *  plain tickets. An order can hold both for the same event under different
   *  change rules, so every exchange call has to carry the same value: it
   *  selects both the rule set and which of the old tickets are handed back. */
  subscriptionTypeId?: number;
  /** Number of tickets being exchanged. */
  ticketCount: number;
  /** ticketTypeId → how many of the exchanged tickets hold that type, so the
   *  new event's seats are preselected with the same ticket types. */
  ticketTypeCounts?: Record<number, number>;
  /** Ticket type ids the exchange rules restrict the new tickets to. Absent or
   *  empty means every ticket type on the new event may be picked. */
  allowedTicketTypeIds?: number[];
  /** priceZoneId → how many of the exchanged tickets sit in that zone, used to
   *  seed the new event's seat search (and to pin it when `samePriceZone`). */
  priceZoneCounts?: Record<number, number>;
  /** Price zone ids the exchange rules restrict the new tickets to, sent only
   *  when `samePriceZone` is on. Absent and empty mean the same thing: the rules
   *  carry no list of their own, and `samePriceZone` then decides whether
   *  `priceZoneCounts` restricts the picker in its place. So empty does not by
   *  itself leave every zone pickable. */
  allowedPriceZoneIds?: number[];
  /** Exchange rule: the new seats must sit in the same price zone as the ones
   *  being exchanged. The backend expresses it by filling `allowedPriceZoneIds`,
   *  so that list is what restricts the picker; this flag only selects the
   *  `priceZoneCounts` fallback for when the list is missing or empty. */
  samePriceZone?: boolean;
  /** The newly chosen event (exchanging TO). */
  selectedEvent?: ExchangeSelectedEvent;
  /** Encrypted id of the staging exchange order, set once the exchange starts.
   *  Identifies the exchange order in the cart, checkout and payment steps. */
  exchangeOrderId?: string;
  /** Refund target chosen on the exchange cart when the swap is cheaper. */
  refundGiftCardGroupId?: number | null;
  refundDonationId?: number | null;
  /** Epoch ms when the exchange flow was started (for timeout handling). */
  createdAt: number;
};

/** Per-tab context for a plain ticket refund (NEWBUY-904/905): carried from the
 *  refund method/target picker into the gift card or donation landing page that
 *  settles it, the same way `TempExchangeData` carries an exchange's refund leg.
 *  Unlike an exchange refund, this never produces a new order — the amount is
 *  fixed to what the refund summary quoted, not chosen on the landing page. */
export type TempTicketRefundData = {
  /** Encrypted id of the order the tickets are being refunded from. */
  encryptedOrderId: string;
  /** The event whose tickets are being refunded. */
  eventId: number;
  /** The fixed refund total, from `RefundSummaryResponse.total`. */
  amount: number;
  /** Gift card group / donation ids the refund summary allows settling as —
   *  a landing page only offers refund mode when its own id is among these. */
  refundGiftCardGroupIds: number[];
  refundDonationIds: number[];
  /** Epoch ms when the refund flow was started (for timeout handling). */
  createdAt: number;
};
export type TempSubscriptionReservationData = {
  subscriptionTypeId: number;
  /** ticketTypeId → quantity */
  quantities: Record<number, number>;
  /** Chosen events from the choose-events step */
  selectedEvents: SubscriptionSelectedEvent[];
  /** eventId → reserved tickets from the seat picker */
  eventSeats: Record<number, TicketDto[]>;
  /** eventId → sorted ticket IDs at the time the user clicked "Next event".
   *  Used to show the check icon only after explicit confirmation and to
   *  reset it when the user goes back and changes seats. */
  confirmedEventSeats?: Record<number, number[]>;
  /** Epoch ms when this subscription reservation was first created (for 12h timeout). */
  createdAt: number;
};

/** Derive order ticket IDs from the live order state instead of duplicating in temp data. */
export function getOrderTicketIds(order: OrderResponse | undefined, eventId: number | undefined): Set<number> {
  if (!order || !eventId) return new Set();
  // Includes tickets bought directly and those nested under subscription
  // groups, so subscription order tickets are recognised when editing seats.
  const tickets = getAllEventTicketGroups(order).find(g => g.eventId === eventId)?.tickets ?? [];
  return new Set(tickets.map(t => t.id).filter((id): id is number => id != null));
}
type EventData = {
  event?: EventDto;
  order?: OrderResponse;
};

/** The order a reservation or renewal flow is paying, held apart from the
 *  buyer's cart. Keyed by the id in the flow's route, which the order keeps
 *  through a split. */
type DedicatedOrderSlot = {
  encryptedOrderId: string;
  order: OrderResponse | null;
};

// Login-identifier values (Email/Mobile) captured at the checkout sign-up step, carried to the
// sign-up details page so the final signup call has them without re-asking. Held in memory only.
export type SignupDraftAttribute = {
  name: string;
  value: string;
};

/** Lightweight type for compiled react-intl messages (avoids inferring the full JSON literal type). */
type Locale = Record<string, MessageFormatElement[]>;
type LocaleKey = keyof typeof allLocales;
interface AppStateContext {
  appSettings: AppSettingsDto | null;
  setAppSettings(appSettings: AppSettingsDto): void;
  appSettingsReady: boolean;
  setAppSettingsReady(ready: boolean): void;
  locale: LocaleKey;
  setLocale(locale: LocaleKey): void;
  currency: string;
  setCurrency(currency: string): void;
  messages: Locale;
  /** The order being worked on with its event. Inside a reservation or
   *  renewal flow (see `lib/dedicated-order`) `order` is the flow's order and
   *  writes land on it; everywhere else it is the buyer's cart. */
  setEventData: Dispatch<SetStateAction<EventData>>;
  eventData: EventData;
  /** Whether the app is in a reservation flow, so `eventData.order` is the
   *  reservation the buyer opened from My pages. */
  isReservationOrder: boolean;
  /** Whether the app is in a renewal flow, so `eventData.order` is the
   *  subscription renewal the Box Office offered the buyer. */
  isRenewalOrder: boolean;
  /** The order the last reservation or renewal flow held, whichever route is
   *  showing — the receipt has to tell it apart from the cart. */
  dedicatedOrder: OrderResponse | undefined;
  /** Seeds the flow for `encryptedOrderId` with a freshly loaded order. */
  setDedicatedOrder: (encryptedOrderId: string, order: OrderResponse) => void;
  /** Lets go of the flow's order, leaving the cart alone. */
  clearDedicatedOrder: () => void;
  /** Items the buyer has removed from the reservation in the cart but that are
   *  still held by it, keyed as in `pay-selection-helpers`. See
   *  `useReservationRemovals`. */
  removedOrderItemKeys: string[];
  setRemovedOrderItemKeys: Dispatch<SetStateAction<string[]>>;
  setTempEventReservationData: Dispatch<SetStateAction<TempEventReservationData | null>>;
  tempEventReservationData: TempEventReservationData | null;
  setTempSubscriptionReservationData: Dispatch<SetStateAction<TempSubscriptionReservationData | null>>;
  tempSubscriptionReservationData: TempSubscriptionReservationData | null;
  // Seat state for editing a subscription that is already in an order (seats
  // reached from the cart). Kept separate from `tempSubscriptionReservationData`
  // so the choose-events / new-subscription flow never sees the existing order.
  setTempSubscriptionOrderEdit: Dispatch<SetStateAction<TempSubscriptionReservationData | null>>;
  tempSubscriptionOrderEdit: TempSubscriptionReservationData | null;
  setTempExchangeData: Dispatch<SetStateAction<TempExchangeData | null>>;
  tempExchangeData: TempExchangeData | null;
  setTempTicketRefundData: Dispatch<SetStateAction<TempTicketRefundData | null>>;
  tempTicketRefundData: TempTicketRefundData | null;
  setTitle: Dispatch<SetStateAction<React.ReactNode | null>>;
  title: React.ReactNode;
  setProgress: Dispatch<SetStateAction<number | null>>;
  progress: number | null;
  navbarContent: React.ReactNode;
  setNavbarContent: Dispatch<SetStateAction<React.ReactNode>>;
  navbarExtraHeight: number;
  setNavbarExtraHeight: Dispatch<SetStateAction<number>>;
  banner: React.ReactNode;
  setBanner: Dispatch<SetStateAction<React.ReactNode>>;
  navbarBannerHeight: number;
  setNavbarBannerHeight: Dispatch<SetStateAction<number>>;
  setTokens: Dispatch<SetStateAction<TokensResponse | null>>;
  setIsCartOpen: Dispatch<SetStateAction<boolean>>;
  isCartOpen: boolean;
  tokens: TokensResponse | null;
  signupDraft: SignupDraftAttribute[] | null;
  setSignupDraft: Dispatch<SetStateAction<SignupDraftAttribute[] | null>>;
}
const defaultAppState = {
  appSettings: null as AppSettingsDto | null,
  setAppSettings: () => void 0,
  appSettingsReady: false,
  setAppSettingsReady: () => void 0,
  locale: UiLocale.En as LocaleKey,
  setLocale: () => void 0,
  currency: 'DKK',
  setCurrency: () => void 0,
  messages: allLocales[UiLocale.En as LocaleKey],
  setEventData: () => void 0,
  eventData: {
    event: undefined,
    order: undefined
  },
  isReservationOrder: false,
  isRenewalOrder: false,
  dedicatedOrder: undefined,
  setDedicatedOrder: () => void 0,
  clearDedicatedOrder: () => void 0,
  removedOrderItemKeys: [] as string[],
  setRemovedOrderItemKeys: () => void 0,
  setTempEventReservationData: () => void 0,
  tempEventReservationData: null,
  setTempSubscriptionReservationData: () => void 0,
  tempSubscriptionReservationData: null,
  setTempSubscriptionOrderEdit: () => void 0,
  tempSubscriptionOrderEdit: null,
  setTempExchangeData: () => void 0,
  tempExchangeData: null,
  setTempTicketRefundData: () => void 0,
  tempTicketRefundData: null,
  setTitle: () => void 0,
  title: undefined,
  setProgress: () => void 0,
  progress: null,
  navbarContent: null,
  setNavbarContent: () => void 0,
  navbarExtraHeight: 0,
  setNavbarExtraHeight: () => void 0,
  banner: null,
  setBanner: () => void 0,
  navbarBannerHeight: 0,
  setNavbarBannerHeight: () => void 0,
  setTokens: () => void 0,
  setIsCartOpen: () => void 0,
  isCartOpen: false,
  tokens: null,
  signupDraft: null as SignupDraftAttribute[] | null,
  setSignupDraft: () => void 0
};
const AppState = createContext<AppStateContext>(defaultAppState);
export const useAppState = () => useContext(AppState);
export function AppStateProvider({
  initialAppSettings,
  children
}: {
  initialAppSettings?: AppSettingsDto | null;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const [appSettings, setAppSettings] = useState<AppSettingsDto | null>(initialAppSettings ?? null);
  // Keep state in sync if a later render delivers a different value (e.g. a
  // dev fallback that initially returned null but later succeeded). Mirrors
  // secure-wallet's AppProviders useEffect that pushes serverAppConfig into
  // the store on prop change.
  useEffect(() => {
    setAppSettings(initialAppSettings ?? null);
  }, [initialAppSettings]);
  const [appSettingsReady, setAppSettingsReady] = useState(false);
  const [locale, setLocale] = useState<LocaleKey>(() => {
    const shortLocale = router.locale;
    return shortLocale ? shortToFullLocale(shortLocale) : defaultAppState.locale;
  });
  // Read synchronously so AppSettingsLoader sees a returning user's stored
  // choice on mount rather than overwriting it before hydration.
  const [currency, setCurrency] = useLocalStorage<string>({
    key: '@currency',
    getInitialValueInEffect: false
  });
  const [messages, setMessages] = useState(allLocales[locale]);
  const [progress, setProgress] = useState<number | null>(null);
  const [title, setTitle] = useState<React.ReactNode>();
  const [navbarContent, setNavbarContent] = useState<React.ReactNode>(null);
  const [navbarExtraHeight, setNavbarExtraHeight] = useState<number>(0);
  const [banner, setBanner] = useState<React.ReactNode>(null);
  const [navbarBannerHeight, setNavbarBannerHeight] = useState<number>(0);
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  // Read synchronously, like `@currency` above: `useAuthSession` treats the
  // absence of a token as "signed out", so a first render that reported null
  // before the effect landed would flash the sign-in UI at a signed-in user.
  const [tokens, setTokens] = useLocalStorage<TokensResponse | null>({
    key: '@tokens',
    defaultValue: null,
    getInitialValueInEffect: false
  });
  // TODO
  // const router = useRouter();
  // useEffect(() => {
  //   if (!router.pathname.startsWith('/event/[eventId]')) {
  //     setProgress(progressBarValues._RESET);
  //   }
  // }, [router.pathname]);

  useEffect(() => {
    setMessages(allLocales[locale]);
  }, [locale]);
  const handleLocaleChange = (newLocale: LocaleKey) => {
    setLocale(newLocale);
    // Change locale in Next.js router using short locale
    const shortLocale = fullToShortLocale(newLocale);
    if (router.locale !== shortLocale) {
      router.push(router.asPath, router.asPath, {
        locale: shortLocale
      });
    }
  };

  // `eventData` is split across two stores to enable different sessions of seat picker for different events.
  const [viewedEvent, setViewedEvent] = useSessionStorage<EventDto | null>({
    key: '@viewedEvent',
    defaultValue: null
  });
  const [order, setOrder] = useLocalStorage<OrderResponse | null>({
    key: '@order',
    defaultValue: null
  });
  // A reservation or renewal flow works on its own order so the cart above is
  // never touched by it. Per tab: the flow lives in this tab's routes, and
  // another tab carries on with the cart.
  const [storedDedicatedSlot, setDedicatedSlot] = useSessionStorage<DedicatedOrderSlot | null>({
    key: '@dedicatedOrder',
    defaultValue: null
  });
  // `createStorage` falls back to an empty string when the key is absent.
  const dedicatedSlot = storedDedicatedSlot || null;
  const dedicatedRoute = parseDedicatedOrderRoute(router.pathname, {
    encryptedOrderId: router.query.encryptedOrderId
  });
  const dedicatedRouteId = dedicatedRoute?.encryptedOrderId;
  const isDedicatedRoute = !!dedicatedRoute;

  // Inside a flow only the slot loaded for the route's order counts; a slot
  // left over from another one, or none yet, is no order rather than the cart.
  const activeOrder = isDedicatedRoute ? dedicatedSlot && dedicatedSlot.encryptedOrderId === dedicatedRouteId ? dedicatedSlot.order ?? undefined : undefined : order || undefined;

  // Normalize the empty-string fallback from `createStorage` and the cleared `null` back to `undefined`
  // so consumers keep seeing `EventDto | undefined` / `OrderResponse | undefined`.
  const eventData = useMemo<EventData>(() => ({
    event: viewedEvent || undefined,
    order: activeOrder
  }), [viewedEvent, activeOrder]);
  const isReservationOrder = dedicatedRoute?.kind === 'reservation' && !!eventData.order;
  const isRenewalOrder = dedicatedRoute?.kind === 'renewal' && !!eventData.order;

  // Ref so the functional updater form of `setEventData` reads current values
  // without forcing the callback to change identity on every event/order write.
  const eventDataRef = useRef(eventData);
  eventDataRef.current = eventData;
  const dedicatedRouteRef = useRef({
    isDedicatedRoute,
    dedicatedRouteId
  });
  dedicatedRouteRef.current = {
    isDedicatedRoute,
    dedicatedRouteId
  };
  const storedOrdersRef = useRef({
    cart: order,
    slot: dedicatedSlot
  });
  storedOrdersRef.current = {
    cart: order,
    slot: dedicatedSlot
  };
  const setEventData = useCallback<Dispatch<SetStateAction<EventData>>>(update => {
    const next = typeof update === 'function' ? update(eventDataRef.current) : update;
    setViewedEvent(next.event ?? null);
    const route = dedicatedRouteRef.current;
    const {
      cart,
      slot
    } = storedOrdersRef.current;
    // A response can land after the buyer has moved between the cart and a
    // flow, so an order goes back to the one it belongs to rather than over
    // whichever the route now shows. A cleared order carries no id and
    // lands on the route's.
    const nextId = next.order?.encryptedId;
    const isCartOrder = !!nextId && nextId === cart?.encryptedId;
    const isSlotOrder = !!nextId && nextId === slot?.order?.encryptedId;
    const writeSlot = (encryptedOrderId: string | undefined) => {
      if (encryptedOrderId) {
        setDedicatedSlot({
          encryptedOrderId,
          order: next.order ?? null
        });
      }
    };
    if (!route.isDedicatedRoute) {
      if (isSlotOrder && !isCartOrder) writeSlot(slot?.encryptedOrderId);else setOrder(next.order ?? null);
      return;
    }
    // The flow's order keeps its id through every change, a split included,
    // so anything else was made for the cart or for an earlier flow. Only
    // the cart's own order, or a first one for an empty cart, goes there.
    if (!nextId || nextId === route.dedicatedRouteId) {
      writeSlot(route.dedicatedRouteId);
    } else if (isSlotOrder) {
      writeSlot(slot?.encryptedOrderId);
    } else if (isCartOrder || !cart) {
      setOrder(next.order ?? null);
    }
  }, [setViewedEvent, setOrder, setDedicatedSlot]);
  const setDedicatedOrder = useCallback((encryptedOrderId: string, next: OrderResponse) => setDedicatedSlot({
    encryptedOrderId,
    order: next
  }), [setDedicatedSlot]);
  const clearDedicatedOrder = useCallback(() => setDedicatedSlot(null), [setDedicatedSlot]);

  // A persisted order that is no longer a live reservation (sold/paid) is
  // dropped so it can't keep the reservation countdown ticking after checkout or
  // while browsing My Pages. The order is shared in localStorage, so this
  // self-heals stale orders from any source (e.g. one claimed via external
  // login).
  useEffect(() => {
    if (order && !isActiveReservation(order)) {
      setOrder(null);
    }
  }, [order, setOrder]);

  // The same for the flow's order, keeping the slot so the flow knows the
  // order it was opened for has gone.
  const dedicatedSlotOrder = dedicatedSlot?.order;
  const dedicatedSlotId = dedicatedSlot?.encryptedOrderId;
  useEffect(() => {
    if (dedicatedSlotId && dedicatedSlotOrder && !isActiveReservation(dedicatedSlotOrder)) {
      setDedicatedSlot({
        encryptedOrderId: dedicatedSlotId,
        order: null
      });
    }
  }, [dedicatedSlotId, dedicatedSlotOrder, setDedicatedSlot]);

  // Per-tab: the in-progress seat selection is isolated so two tabs viewing the
  // same event don't fight over each other's reservations. The order itself
  // (in `eventData`) stays shared in localStorage — there is only one order.
  const [tempEventReservationData, setTempEventReservationData] = useSessionStorage<{
    eventId: number;
    tickets: TicketDto[];
  } | null>({
    key: '@tempEventReservationData',
    defaultValue: null
  });

  // Per-tab, like the event seat selection: two tabs building different
  // subscriptions must not share an in-progress selection.
  const [tempSubscriptionReservationData, setTempSubscriptionReservationData] = useSessionStorage<TempSubscriptionReservationData | null>({
    key: '@tempSubscriptionReservationData',
    defaultValue: null
  });

  // Seat state while editing a subscription already in an order. Held in memory
  // (not persisted): it is rebuilt from the live order each time the picker is
  // opened from the cart, so there is nothing to restore across a reload.
  const [tempSubscriptionOrderEdit, setTempSubscriptionOrderEdit] = useState<TempSubscriptionReservationData | null>(null);

  // Per-tab, like the event seat selection: an in-progress ticket exchange must
  // not leak between tabs handling different orders.
  const [tempExchangeData, setTempExchangeData] = useSessionStorage<TempExchangeData | null>({
    key: '@tempExchangeData',
    defaultValue: null
  });

  // Per-tab, like the exchange's own refund leg: a plain ticket refund in
  // progress must not leak between tabs handling different orders.
  const [tempTicketRefundData, setTempTicketRefundData] = useSessionStorage<TempTicketRefundData | null>({
    key: '@tempTicketRefundData',
    defaultValue: null
  });
  const [signupDraft, setSignupDraft] = useState<SignupDraftAttribute[] | null>(null);

  // What the buyer has removed from the reservation in the cart, not yet
  // committed to the backend. Held in memory (not persisted) so a reload gives
  // back the untouched reservation.
  const [removedOrderItemKeys, setRemovedOrderItemKeys] = useState<string[]>([]);

  // Removals belong to one reservation or renewal. Swapping the cart for
  // another order, or for a basket the buyer built themselves, drops them
  // rather than leaving keys behind that would hide items off an unrelated
  // order.
  const removalsOrderIdRef = useRef<string | null>(null);
  const removalsOrderId = isReservationOrder || isRenewalOrder ? eventData.order?.encryptedId ?? null : null;
  useEffect(() => {
    if (removalsOrderId === removalsOrderIdRef.current) return;
    removalsOrderIdRef.current = removalsOrderId;
    setRemovedOrderItemKeys(current => current.length ? [] : current);
  }, [removalsOrderId]);

  // Auto-clear subscription reservation data after 12 hours
  useTempDataExpiry(tempSubscriptionReservationData ?? null, setTempSubscriptionReservationData);

  // Same for a ticket change: it holds a staging order id the server reaps once
  // the reservation expires, so stale data would render the cart against an
  // order that no longer exists.
  useTempDataExpiry(tempExchangeData ?? null, setTempExchangeData);

  // Same for a plain ticket refund: it references a refund summary quote that
  // the server would re-derive fresh anyway, so stale data has no value.
  useTempDataExpiry(tempTicketRefundData ?? null, setTempTicketRefundData);

  // Opening a different subscription drops any in-progress selection from a
  // previous one, so a stale reservation never carries into the new flow.
  const routeSubscriptionTypeId = parseNumericQueryParam(router.query.subscriptionTypeId);
  const storedSubscriptionTypeId = tempSubscriptionReservationData?.subscriptionTypeId;
  useEffect(() => {
    if (typeof routeSubscriptionTypeId === 'number' && typeof storedSubscriptionTypeId === 'number' && storedSubscriptionTypeId !== routeSubscriptionTypeId) {
      setTempSubscriptionReservationData(null);
    }
  }, [routeSubscriptionTypeId, storedSubscriptionTypeId, setTempSubscriptionReservationData]);
  return <AppState.Provider value={{
    locale,
    setLocale: handleLocaleChange,
    currency: currency ?? defaultAppState.currency,
    setCurrency,
    messages,
    appSettings,
    setAppSettings,
    appSettingsReady,
    setAppSettingsReady,
    eventData: eventData ?? defaultAppState.eventData,
    setEventData,
    isReservationOrder,
    isRenewalOrder,
    dedicatedOrder: dedicatedSlotOrder ?? undefined,
    setDedicatedOrder,
    clearDedicatedOrder,
    removedOrderItemKeys,
    setRemovedOrderItemKeys,
    tempEventReservationData: tempEventReservationData ?? defaultAppState.tempEventReservationData,
    setTempEventReservationData,
    tempSubscriptionReservationData: tempSubscriptionReservationData ?? defaultAppState.tempSubscriptionReservationData,
    setTempSubscriptionReservationData,
    tempSubscriptionOrderEdit,
    setTempSubscriptionOrderEdit,
    tempExchangeData: tempExchangeData as unknown !== '' ? tempExchangeData ?? null : null,
    setTempExchangeData,
    tempTicketRefundData: tempTicketRefundData as unknown !== '' ? tempTicketRefundData ?? null : null,
    setTempTicketRefundData,
    progress,
    setProgress,
    title,
    setTitle,
    navbarContent,
    setNavbarContent,
    navbarExtraHeight,
    setNavbarExtraHeight,
    banner,
    setBanner,
    navbarBannerHeight,
    setNavbarBannerHeight,
    setIsCartOpen,
    isCartOpen,
    tokens: tokens as any !== '' ? tokens ?? null : null,
    setTokens,
    signupDraft,
    setSignupDraft
  }}>
      {children}
    </AppState.Provider>;
}